import { tabName, TabName } from 'src/app/infrastructure/services/page-services/models/tab-name';

// Eén bezocht item in de navigation-history. Platte lijst; de accordion-UI leidt
// de boomstructuur (domein → project → child) af met computed-selectors. De entry
// wordt gesynct naar de backend (deel van de User-state-blob).

export const navCategory = {
    // admin-niveau (alleen voor administrators)
    pages: 'pages',
    tabs: 'tabs',
    functions: 'functions',
    roles: 'roles',
    activities: 'activities',
    pageComponents: 'pageComponents',
    claims: 'claims',
    claimActions: 'claimActions',
    creditTypes: 'creditTypes',
    themes: 'themes',
    // user-niveau 1/2
    domains: 'domains',
    projects: 'projects',
    domainUsers: 'domainUsers',
    contacts: 'contacts',
    contactTypes: 'contactTypes',
    observationTypes: 'observationTypes',
    taskTypes: 'taskTypes',
    projectCategories: 'projectCategories',
    // user-niveau 3 (project-childs)
    projectMembers: 'projectMembers',
    notices: 'notices',
    tasks: 'tasks',
} as const;

export type TNavCategory = (typeof navCategory)[keyof typeof navCategory];

const __NAV_CATEGORY_VALUES = new Set<string>(Object.values(navCategory));

// True als deze tabName ook een gevolgde geschiedenis-categorie is (zie DOMAIN_TAB_CATEGORY_OVERRIDES
// hieronder voor de paar plekken waar tabName en navCategory afwijken). Gebruikt om een
// tabheader-snapshot-tab te onderscheiden van tabs zonder eigen navigation-history (Photos,
// Description, Invitations, ...).
export function isNavCategory(value: string): value is TNavCategory {
    return __NAV_CATEGORY_VALUES.has(value);
}

export interface INavHistoryEntry {
    category: TNavCategory;
    guid: string;
    label: string;
    photoGuid: string | null;         // null ⇒ leeg kader in de ImageBox
    logoFolderShard: string | null;   // alleen domains: folderShard voor de publieke logo-URL
    logoVersion: string | null;       // alleen domains: cache-bust (modifiedOn van het logo-bestand)
    dGuid: string | null;             // domein-scope (admin-entiteiten ⇒ null)
    oGuid: string | null;             // project-scope (alleen level3: projectMembers/notices)
    url: string;                      // router.url op moment van bezoek → klik navigeert hierheen
    visitedAt: number;                // epoch ms; sorteren + 'nieuwste wint' bij cross-device merge
    // Alleen category === domainUsers: true zolang photoGuid via de User.PhotoFileBoxId-fallback komt
    // (geen eigen DomainUser.ProfileFileBoxId gekozen, zie __domainUserPhotoGuid in
    // nav-history-descriptors.ts). Laat NavigationHistoryService.userPhotoGuid_propagate() weten welke
    // domainUsers-entries mee moeten met een Home-hoofdfoto-wijziging (Home heeft zelf geen
    // nav-history-entry, zie HomeComponent.emit_ownMainPhotoChanged) en welke niet (een eigen
    // domain-specifieke keuze mag niet overschreven worden).
    photoIsUserFallback?: boolean;

    // Alleen category === domainUsers/projectMembers (zie __domainUserEmail/__projectMemberEmail in nav-history-descriptors.ts): e-mailadres
    // van de User achter dit DomainUser-record, getoond als sublabel onder de naam in de drawer (zie
    // NavigationHistoryService.__itemNode / INavTreeNode.sublabel). Voor overige categorieën ongezet.
    email?: string | null;

    // Guid van de bovenliggende pagina: een taak boven een taak (null = hoofdtaak) of boven een melding (fase 3c). De
    // drawer nest bezochte entries daarmee onder hun ouder (NavigationHistoryService.__itemNodes) en capt per ouder, niet
    // per project.
    parentGuid?: string | null;
    // Categorie van die ouder als die anders is dan de eigen (melding in een taak). Zo'n entry hoort niet in de lijst
    // van het project zelf, alleen onder de tab van zijn ouder.
    parentCategory?: TNavCategory | null;
}

// Max items per (category, scope) — zo: max 5 projects per domein, max 5 notices per
// project, max 5 domeinen, enz.
export const NAV_HISTORY_MAX_PER_GROUP = 5;

// Volgorde van onderstaande terugval-lijsten = volgorde van de tabs in de sourceList van de
// container (AdminComponent/DomainComponent/ProjectComponent), zodat de drawer ook zonder snapshot
// dezelfde volgorde toont als de tab-dropdown in de tabheader. Bij het verschuiven van tabs daar
// deze lijsten meeverhuizen.

// Admin-accordion: child-categorieën onder het admin-domein-paneel.
export const ADMIN_CATEGORIES: readonly TNavCategory[] = [
    navCategory.domainUsers, navCategory.functions, navCategory.roles, navCategory.activities,
    navCategory.pages, navCategory.tabs, navCategory.claims, navCategory.pageComponents,
    navCategory.claimActions, navCategory.themes, navCategory.creditTypes,
];

// User-accordion level2: child-categorieën onder een domein-paneel ('projects' is
// tevens parent voor level3 en wordt in de UI apart/uitklapbaar getoond).
export const USER_L2_CATEGORIES: readonly TNavCategory[] = [
    navCategory.domainUsers, navCategory.functions, navCategory.contacts,
    navCategory.contactTypes, navCategory.observationTypes, navCategory.taskTypes, navCategory.projectCategories,
];

// User-accordion level3: child-categorieën onder een project-paneel.
export const USER_L3_CATEGORIES: readonly TNavCategory[] = [
    navCategory.notices, navCategory.tasks, navCategory.projectMembers,
];

// Eén tab uit de al-geautoriseerde tablijst van een Domain/Project/AdminPanel-bezoek (zie
// _TabPageRouteComponent.__navHistory_recordTabs) — dezelfde bron als de echte tabheader, dus geen
// losse rechten-check nodig in de navigation-history-drawer.
export interface IContainerTabSnapshot {
    name: TabName;
    label: string;
}

// Op domain-niveau wijkt de tabName af van de navCategory waaronder bezochte items worden
// bijgehouden (bestaande, niet aan te passen wiring van DomainComponent.sourceList$$) — project- en
// admin-niveau hebben dit niet nodig (tabName == navCategory daar 1-op-1).
//
// LET OP: `functionMembers` staat hier BEWUST NIET (meer) in. Domain heeft twee aparte tabs:
// `functionMembers` ("function members", de DomainUser↔Function-toewijzingslijst) én `functions`
// (de Function-entiteiten zelf, tabName == navCategory.functions al zonder override). Een eerdere
// versie mapte `functionMembers` óók naar navCategory.functions (uit de tijd dat dat de enige
// function-gerelateerde tab was) — dat liet `__tabCategoryNode` voor BEIDE tabs dezelfde
// (navCategory.functions-)children tonen als twee aparte koppen, waarbij "function members" won van
// "functions" in `__pathToActive`'s __partial-fallback (eerder in de tab-array, zie
// DomainComponent.sourceList$$) — vandaar dat de drawer na Domain→Functions→een Function
// "FunctionMember" liet uitklappen i.p.v. "Functions". FunctionMember heeft geen eigen
// nav-geschiedenis (geen entry in nav-history-descriptors.ts, net als Photos/Description/
// Invitations) — zonder override toont de tab dus terecht als klikbare regel zonder kinderen.
export const DOMAIN_TAB_CATEGORY_OVERRIDES: Partial<Record<TabName, TNavCategory>> = {
    [tabName.projectMembers]: navCategory.projects,        // Domain's "Projects"-tab heet intern projectMembers
    [tabName.categories]: navCategory.projectCategories,
};

// Op project-niveau heet de takentab intern projectTasks; bezochte taken staan onder navCategory.tasks.
export const PROJECT_TAB_CATEGORY_OVERRIDES: Partial<Record<TabName, TNavCategory>> = {
    [tabName.projectTasks]: navCategory.tasks,
};

// Tabs van een bezochte pagina (niet-container, zie _TabPageRouteComponent.__navHistory_recordTabs) die zelf een
// gevolgde categorie tonen: de items eronder zijn bezochte entries met parentGuid = die pagina (taak in een taak).
export const ITEM_TAB_CATEGORY_OVERRIDES: Partial<Record<TabName, TNavCategory>> = {
    [tabName.taskSubtasks]: navCategory.tasks,
    [tabName.taskNotices]: navCategory.notices,
};

// Koppen per categorie (NL). Los van de entries zodat templates geen losse strings bevatten.
export const NAV_CATEGORY_LABEL: Record<TNavCategory, string> = {
    [navCategory.pages]: 'Pagina\'s',
    [navCategory.tabs]: 'Tabs',
    [navCategory.functions]: 'Functies',
    [navCategory.roles]: 'Rollen',
    [navCategory.activities]: 'Activiteiten',
    [navCategory.pageComponents]: 'Paginacomponenten',
    [navCategory.claims]: 'Claims',
    [navCategory.claimActions]: 'Claim-acties',
    [navCategory.creditTypes]: 'Credit types',
    [navCategory.themes]: 'Thema\'s',
    [navCategory.domains]: 'Domeinen',
    [navCategory.projects]: 'Projects',
    [navCategory.domainUsers]: 'Domeingebruikers',
    [navCategory.contacts]: 'Contacten',
    [navCategory.contactTypes]: 'Contacttypes',
    [navCategory.observationTypes]: 'Observatietypes',
    [navCategory.taskTypes]: 'Taaktypes',
    [navCategory.projectCategories]: 'Projectcategorieën',
    [navCategory.projectMembers]: 'Projectleden',
    [navCategory.notices]: 'Meldingen',
    [navCategory.tasks]: 'Taken',
};
